import React, { forwardRef } from 'react';

import { observable } from 'mobx';

import { ClickAwayListener, IconButton, MenuItem, Tooltip } from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';

import { ExpandMore, ExpandLess } from '@mui/icons-material';

import Box from '@mui/material/Box';

import { StyledTextField } from 'components/Input/styles';

import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import { Header, FixedLabel, StyledPopover } from './styles';

import { ContainerHeaderProps } from './types';

const ContainerHeader = forwardRef<HTMLDivElement, ContainerHeaderProps>(
  ({ expanded, setExpanded, label, onRemove, fixed, fixedTooltip, id }, ref) => {
    const { entityLayout } = useStore();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [editSectionTitle, setEditSectionTitle] = React.useState(false);
    const [sectionTitle, setSectionTitle] = React.useState(label || '');

    const handleClickOptions = (event: React.MouseEvent<HTMLButtonElement>): void => {
      if (anchorEl) {
        setAnchorEl(null);
      } else {
        setAnchorEl(event.currentTarget);
      }
    };

    const handleExpandClick = (): void => {
      setExpanded((prevState) => !prevState);
    };

    const handleRenameSection = (): void => {
      entityLayout.renameSection(id, sectionTitle);
      setEditSectionTitle(false);
    };

    const handleDiscardChanges = (): void => {
      setSectionTitle(label || '');
      setEditSectionTitle(false);
    };

    // Dropped entirely rather than nulled, so a fixed title is not an interactive element at all.
    const titleInteraction = fixed
      ? {}
      : { onClick: () => setEditSectionTitle(true), tabIndex: 0, role: 'button' as const };

    return (
      <Header $expanded={expanded} ref={ref}>
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 3 }}>
          {expanded ? (
            <ExpandLess onClick={handleExpandClick} aria-label="show less" color="disabled" />
          ) : (
            <ExpandMore onClick={handleExpandClick} aria-label="show more" color="disabled" />
          )}
          {editSectionTitle && !fixed ? (
            <ClickAwayListener onClickAway={handleDiscardChanges}>
              <StyledTextField
                fullWidth
                size="small"
                value={sectionTitle}
                sx={{ backgroundColor: colors.common.white, margin: '0 8px' }}
                onChange={(event) => setSectionTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleRenameSection();
                  }
                }}
              />
            </ClickAwayListener>
          ) : (
            <div {...titleInteraction} data-testid="section-title">
              <p style={{ marginLeft: '8px' }} id={`section-title-${sectionTitle}`}>
                {sectionTitle}
              </p>
            </div>
          )}
          {fixed && (
            <Tooltip title={fixedTooltip} placement="bottom">
              <FixedLabel data-testid="fixed-section-label">
                <LockIcon />
                Fixed
              </FixedLabel>
            </Tooltip>
          )}
        </Box>
        {!fixed &&
          (editSectionTitle ? (
            <Box sx={{ display: 'flex', justifyContent: 'end' }}>
              <IconButton onClick={handleDiscardChanges}>
                <CloseIcon sx={{ width: '16px', height: '16px' }} htmlColor={colors.text.secondary} />
              </IconButton>
              <IconButton onClick={handleRenameSection}>
                <CheckOutlinedIcon sx={{ width: '16px', height: '16px' }} htmlColor={colors.text.secondary} />
              </IconButton>
            </Box>
          ) : (
            <IconButton onClick={handleClickOptions} sx={{ p: 0 }}>
              <MoreVertIcon htmlColor={colors.text.secondary} />
            </IconButton>
          ))}

        {onRemove && !fixed && (
          <StyledPopover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            id="popover-remove-rename-section"
            data-testid="popover-remove-rename-section"
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            <MenuItem id="remove-section-button" onClick={onRemove}>
              Delete
            </MenuItem>
          </StyledPopover>
        )}
      </Header>
    );
  }
);

export default observable(ContainerHeader);
